import type { Response } from 'express';
import { getDeployTarget } from '../../utils/url-utils';

/**
 * Determines whether HTML caching should be bypassed (no-cache headers) based on the deploy target
 * and the optional local override flag.
 *
 * Local and sandbox deployments default to bypassing cache to avoid stale HTML during development.
 * Setting ENABLE_LOCAL_HTML_CACHE="true" overrides this, allowing developers to opt-in to caching locally.
 */
const shouldBypassCache = (): boolean => {
    const deployTarget = (getDeployTarget() || '').toLowerCase();
    const isLocalEnvironment =
        deployTarget.startsWith('local') || deployTarget.startsWith('sandbox');

    if (!isLocalEnvironment) {
        return false;
    }

    const localCacheFlag =
        (typeof process !== 'undefined' &&
            process.env?.ENABLE_LOCAL_HTML_CACHE?.toLowerCase() === 'true') ||
        false;

    return !localCacheFlag;
};

/**
 * Configures caching headers for HTML responses, applying no-cache directives for local/sandbox environments
 * unless explicitly overridden, otherwise setting standard public caching headers.
 *
 * @param res Optional Express response object to configure headers on.
 * @param ttl Time-to-live for public cache headers in seconds. Defaults to one hour.
 */
export function configHtmlCache(res?: Partial<Response>, ttl: number = 3600): void {
    if (!res || typeof res.set !== 'function') {
        return;
    }

    if (shouldBypassCache()) {
        res.set('Cache-Control', 'no-store, no-cache, must-revalidate, proxy-revalidate');
        res.set('Pragma', 'no-cache');
        res.set('Expires', '0');
        return;
    }

    res.set('Cache-Control', `public, must-revalidate, max-age=${ttl}`);
    res.setHeader?.(
        'Accept-CH',
        'Sec-CH-Viewport-Width,Sec-CH-Downlink,Sec-CH-UA,Sec-CH-UA-Platform,Sec-CH-UA-Mobile',
    );
}
